import React, { useState } from 'react';
import { ReactComponent as LogoutIcon } from './icons/logout.svg';
import { ReactComponent as ArrowDownIcon } from 'assets/icons/arrow-down-icon.svg';
import { Link, useNavigate } from 'react-router-dom';
import { ReactComponent as OrderHistoryIcon } from './icons/order-history.svg';
import { ReactComponent as ExportOrdersIcon } from './icons/export-orders.svg';
import { ReactComponent as StarIcon } from '../../assets/icons/star-filled.svg';
import { ReactComponent as PauseIcon } from 'assets/icons/pause-icon.svg';
import DisplayNameIcon from '../base/display-name-icon';
import ExportOrdersModal from '../logistics/export-orders';
import TutorialModal from '../logistics/tutorial-modal';
import { Dropdown, DropdownItem, DropdownList } from '@cokitchen/cokitchen-components';
import {
  CS_ROUTE_KEYS,
  KITCHEN_ROUTE_KEYS,
  LOGISTICS_ROUTE_KEYS,
  ROUTE_KEYS,
  STORAGE_KEYS
} from 'utils/constants';
import { getCurrentTeam } from 'utils/role-config';
import { SettingsIcon } from 'cokitchen-icons';
import useRoleContext from 'contexts/role.context';
import AdminRoleDropdown from './admin-role-dropdown';
import NotificationDropdown from './notification/notification-dropdown';
import SplitScreen from 'pages/kitchen/split-screen';
import { ENUM_COKITCHEN_TEAM, ENUM_ROLE } from '@cokitchen/cokitchen-auth';

type UserDropdownObj = { name: string; route?: string; onClick?: () => void; icon: JSX.Element };

const User = () => {
  const [logoutModal, setLogoutModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showHowToUseModal, setShowHowToUseModal] = useState(false);
  const { isMainAdmin, role, setRole, setIsMainAdmin } = useRoleContext();
  const [showSplitScreen, setShowSplitScreen] = useState(false);

  const navigate = useNavigate();

  const toggleModal = () => {
    setLogoutModal(!logoutModal);
  };

  const toggleSplitScreenModal = () => {
    setShowSplitScreen(!showSplitScreen);
  };
  const logout = () => {
    localStorage.clear();
    setRole();
    setIsMainAdmin(false);
    navigate(ROUTE_KEYS.LOGIN);
  };
  const exportOrders = () => {
    setShowExportModal(!showExportModal);
  };
  const howToUse = () => {
    setShowHowToUseModal(!showHowToUseModal);
  };

  const DROPDOWN_LIST_MAP: Record<ENUM_COKITCHEN_TEAM, UserDropdownObj[]> = {
    cs: [
      {
        name: 'Settings',
        route: CS_ROUTE_KEYS.SETTINGS,
        icon: <SettingsIcon className='w-5 h-5' />
      },
      {
        name: 'Team Statistics',
        route: CS_ROUTE_KEYS.TEAM_STATS,
        icon: <StarIcon className='w-5 h-5' />
      }
    ],
    logistics: [
      {
        name: 'Order history',
        icon: <OrderHistoryIcon />,
        route: LOGISTICS_ROUTE_KEYS.ORDER_HISTORY
      },
      {
        name: 'Team Statistics',
        route: LOGISTICS_ROUTE_KEYS.TEAM_STATS,
        icon: <StarIcon className='w-5 h-5' />
      },
      {
        name: 'FCB Deliveries',
        route: LOGISTICS_ROUTE_KEYS.FCB_DELIVERIES,
        icon: <ExportOrdersIcon />
      },
      // {
      //   name: 'Delivery Managment',
      //   route: LOGISTICS_ROUTE_KEYS.DELIVERY_MANAGMENT,
      //   icon: <RiderIcon />
      // },
      {
        name: 'Export Orders',
        icon: <ExportOrdersIcon />,
        onClick: exportOrders
      }
      // { name: 'How to use', icon: <HowToUseIcon />, onClick: howToUse }
    ],
    kitchen: [
      {
        name: 'Manage Traffic',
        route: KITCHEN_ROUTE_KEYS.MANAGE_TRAFFIC,
        icon: <PauseIcon className='w-5 h-5' />
      },
      {
        name: 'Settings',
        route: KITCHEN_ROUTE_KEYS.SETTINGS,
        icon: <SettingsIcon className='w-5 h-5' />
      },
      // {
      //   name: 'Split Screen',
      //   icon: <SplitIcon className='w-5 h-5' />,
      //   onClick: toggleSplitScreenModal
      // },
      {
        name: 'Team Statistics',
        route: KITCHEN_ROUTE_KEYS.TEAM_STATS,
        icon: <StarIcon className='w-5 h-5' />
      }
    ],
    security: []
  };

  const profileDropdownLinks = () => {
    const team = getCurrentTeam();
    if (!team) return [];
    return [...otherRoleLinks(), ...DROPDOWN_LIST_MAP[team]];
  };

  const firstName = localStorage.getItem(STORAGE_KEYS.FIRST_NAME) ?? '';
  const lastName = localStorage.getItem(STORAGE_KEYS.LAST_NAME) ?? '';

  const user = { first_name: firstName, last_name: lastName };

  const otherRoleLinks = (): UserDropdownObj[] => {
    const hideLogisticsSettings = role === ENUM_ROLE.LOGISTICS_SUPER_ADMIN;

    return hideLogisticsSettings && getCurrentTeam() === ENUM_COKITCHEN_TEAM.LOGISTICS
      ? [
          {
            name: 'Settings',
            route: LOGISTICS_ROUTE_KEYS.SETTINGS,
            icon: <SettingsIcon className='w-5 h-5' />
          }
        ]
      : [];
  };

  return (
    <div className='flex gap-4 items-center'>
      <div>
        <NotificationDropdown />
      </div>
      {isMainAdmin && (
        <div className='hidden md:block'>
          <AdminRoleDropdown />
        </div>
      )}
      <Dropdown
        content={
          <div className=' flex gap-[.6rem] items-center '>
            <DisplayNameIcon user={user} size='large' color='red' />
            <div className=' flex items-center gap-1' role='button' onClick={toggleModal}>
              <div className='hidden lg:flex flex-col gap-[1px]'>
                <h4 className='text-base font-medium uppercase mb-0'>
                  {firstName} {lastName}
                </h4>
                <span className='text-[12px] text-gray-5'>
                  {isMainAdmin ? localStorage.getItem(STORAGE_KEYS.ADMIN_ROLE) : role}
                </span>
              </div>
              <div className='rounded-full text-gray-5 cursor-pointer hover:bg-gray-1 transition-all'>
                <ArrowDownIcon />
              </div>
            </div>
          </div>
        }>
        <DropdownList className='min-w-[190px]'>
          {profileDropdownLinks().map(({ name, icon, onClick, route }, index) => (
            <DropdownItem key={index} className='text-sm font-medium'>
              {route ? (
                <Link to={route} className='items-center flex'>
                  <span className='pr-4'>{icon}</span>
                  {name}
                </Link>
              ) : (
                <button onClick={onClick} className='cursor-pointer items-center flex'>
                  <span className='pr-4'>{icon}</span>
                  {name}
                </button>
              )}
            </DropdownItem>
          ))}
          <DropdownItem className='text-red-5' onClick={logout}>
            <span className='pr-4'>
              <LogoutIcon />
            </span>
            Logout
          </DropdownItem>
        </DropdownList>
      </Dropdown>
      {showExportModal && <ExportOrdersModal show onClose={exportOrders} />}
      {showHowToUseModal && <TutorialModal onClose={howToUse} show />}
      {showSplitScreen && <SplitScreen show onClose={toggleSplitScreenModal} />}
    </div>
  );
};

export default User;
